import { ProductManagementSearchResult } from "src/app/ui/components/pbm/product-management/search-product/product-management-search-result";
import { ProductSubtype } from "src/app/ui/models/domains/pbm/Benefit/product-subtype";
import { ProductType } from "src/app/ui/models/domains/pbm/Benefit/product-type";
import { TherapeuticClass } from "src/app/ui/models/domains/pbm/Benefit/therapeutic-class";
import { FiscalNumber } from "src/app/ui/models/domains/pbm/fiscal-number";
import { Pricing } from "src/app/ui/models/domains/pbm/product-management/pricing";
import { PricingQuotaConfiguration } from "src/app/ui/models/domains/pbm/product-management/pricing-quota-configuration.model";
import { PriceRegime } from "src/app/ui/models/domains/pbm/product-management/price-regime";
import { ProductLabelInfo } from "src/app/ui/models/domains/pbm/product-management/product-label-info";
import { ProductState } from "src/app/ui/models/domains/pbm/product-management/product-state.model";
import { ProductUseValidity } from "src/app/ui/models/domains/pbm/product-management/product-use-validity";
import { TributaryCreditConcession } from "src/app/ui/models/domains/pbm/product-management/tributary-credit-concession";
import { UseType } from "src/app/ui/models/domains/pbm/product-management/use-type";
import { TemplateProductPricing } from "src/app/ui/models/domains/pbm/product-management/template-product-pricing";

export function parseProductManagementSearchResult(payload: any): ProductManagementSearchResult {
  const result = new ProductManagementSearchResult();
  result.setStartIndex(payload.Start);
  result.setEndIndex(payload.TotalPerPages);
  result.setTotal(payload.Total);
  result.setProducts(
    payload.Data.map((item) => {
      return parseProductLabelInfo(item);
    }),
  );
  return result;
}

export function parseProductLabelInfo(payload: any): ProductLabelInfo {
  const therapeuticClass = new TherapeuticClass();
  therapeuticClass.setTherapeuticClassKey(payload.TherapeuticClassId);
  therapeuticClass.setDescription(payload.TherapeuticClass);
  therapeuticClass.setCode(payload.TherapeuticClassCode);
  const product = new ProductLabelInfo();
  product.setProductKey(payload.ProductKey.Value);
  product.setEan1(payload.Ean ? payload.Ean.Value : "");
  product.setEan2(payload.Ean2 ? payload.Ean2.Value : "");
  product.setEan3(payload.Ean3 ? payload.Ean3.Value : "");
  product.setModified(payload.Modified ? payload.Modified : "");
  product.setProductName(payload.ProductName);
  product.setPresentation(payload.Presentation);
  product.setLaboratory(payload.Laboratory);
  product.setSubstance(payload.Substance);
  product.setFiscalNumber(new FiscalNumber(payload.FiscalNumber));
  product.setGGREMCode(payload.GGREMCode);
  product.setRegistry(payload.Registry);
  product.setHospitalRestriction(payload.HospitalRestriction);
  product.setCap(payload.Cap);
  product.setConfaz(payload.Confaz);
  product.setIcms0Percent(payload.Icms0Percent);
  product.setAppealAnalysis(payload.AppealAnalysis);
  product.setCommercialization(payload.Commercialization);
  product.setTarge(payload.Targe);
  product.setProductType(parseProductType(payload.ProductType));
  product.setProductSubType(parseProductSubType(payload.ProductSubType));
  product.setTherapeuticClass(therapeuticClass);
  product.setProductUseValidity(parseProductUseValidity(payload.ProductUseValidity));
  product.setPriceRegime(parsePriceRegime(payload.PriceRegime));
  product.setTributaryCreditConcession(parseTributaryCreditConcession(payload.TributaryCreditConcession));
  product.setUseType(parseUseType(payload.UseType));
  return product;
}

export function parseProductType(payload: any): ProductType {
  const productType = new ProductType();
  productType.setKey(payload.Key);
  productType.setValue(payload.Value);
  return productType;
}

export function parseProductTypes(payload: any): ProductType[] {
  return payload.map((item) => {
    return parseProductType(item);
  });
}

export function parseProductSubType(payload: any): ProductSubtype {
  const productSubType = new ProductSubtype();
  productSubType.setKey(payload.Key);
  productSubType.setValue(payload.Value);
  return productSubType;
}

export function parseProductSubTypes(payload: any): ProductSubtype[] {
  return payload.map((item) => {
    return parseProductSubType(item);
  });
}

export function parseTherapeuticClass(payload: any): TherapeuticClass {
  const therapeuticClass = new TherapeuticClass();
  therapeuticClass.setTherapeuticClassKey(payload.TherapeuticClassKey);
  therapeuticClass.setDescription(payload.Description);
  therapeuticClass.setCode(payload.Code);
  return therapeuticClass;
}

export function parseProductUseValidity(payload: any): ProductUseValidity {
  const productUseValidity = new ProductUseValidity();
  productUseValidity.setKey(payload.Key);
  productUseValidity.setUseType(payload.UseType);
  return productUseValidity;
}

export function parseProductUseValidities(payload: any): ProductUseValidity[] {
  return payload.map((item) => {
    return parseProductUseValidity(item);
  });
}

export function parseProductPricings(payload: any): Pricing[] {
  return payload.map((item) => {
    return parseProductPricing(item);
  });
}

export function parseProductPricing(payload: any): Pricing {
  const pricing = new Pricing();
  pricing.setPricingKey(payload.PricingKey.Value);
  pricing.setMaxPrice(payload.MaxPrice);
  pricing.setPrice(payload.Price);
  pricing.setPricingQuotaConfiguration(parsePricingQuotaConfiguration(payload.PricingQuotaConfiguration));
  pricing.setCreated(payload.Created);
  pricing.setModified(payload.Modified);
  return pricing;
}

export function parsePricingQuotaConfiguration(payload: any): PricingQuotaConfiguration {
  const pricingQuotaConfiguration = new PricingQuotaConfiguration();
  pricingQuotaConfiguration.setPricingQuotaConfigurationKey(payload.PricingQuotaConfigurationKey);
  pricingQuotaConfiguration.setQuota(payload.Quota);
  pricingQuotaConfiguration.setState(parseProductState(payload.State));
  pricingQuotaConfiguration.setFreeCommercialArea(payload.FreeCommercialArea);
  pricingQuotaConfiguration.setCluster(payload.Cluster.Value);
  return pricingQuotaConfiguration;
}

export function parseProductState(payload: any): ProductState {
  const productState = new ProductState();
  productState.setStateKey(payload.StateKey);
  productState.setName(payload.Name);
  productState.setUf(payload.Uf);
  productState.setUfCode(payload.UfCode);
  productState.setDistricts(payload.Districts);
  return productState;
}

export function parsePriceRegime(payload: any): PriceRegime {
  const priceRegime = new PriceRegime();
  priceRegime.setKey(payload.Key);
  priceRegime.setValue(payload.Value);
  return priceRegime;
}

export function parsePriceRegimes(payload: any): PriceRegime[] {
  return payload.map((item) => {
    return parsePriceRegime(item);
  });
}

export function parseUseType(payload: any): UseType {
  const useType = new UseType();
  useType.setKey(payload.Key);
  useType.setValue(payload.Value);
  return useType;
}

export function parseUseTypes(payload: any): UseType[] {
  return payload.map((item) => {
    return parsePriceRegime(item);
  });
}

export function parseTributaryCreditConcession(payload: any): TributaryCreditConcession {
  const tributaryCreditConcession = new TributaryCreditConcession();
  tributaryCreditConcession.setKey(payload.Key);
  tributaryCreditConcession.setValue(payload.Value);
  return tributaryCreditConcession;
}

export function parseTributaryCreditConcessions(payload: any): TributaryCreditConcession[] {
  return payload.map((item) => {
    return parseTributaryCreditConcession(item);
  });
}

export function parseTemplateProductPricingList(payload: any): TemplateProductPricing[] {
  return payload.map((item) => {
    return parseTemplateProductPricing(item);
  });
}

export function parseTemplateProductPricing(payload: any): TemplateProductPricing {
  const template = new TemplateProductPricing();
  template.setPricingQuotaConfigurationKey(payload.PricingQuotaConfigurationKey);
  template.setState(parseProductState(payload.State));
  template.setFreeCommercialArea(payload.FreeCommercialArea);
  template.setQuota(payload.Quota);
  return template;
}
